import { useState } from 'react';
import { useAuth } from '../App';
import { authApi } from '../services/api';
import { formatDate } from '../utils/dateFormatter';
import './AccountSettings.css';

const AccountSettings = () => {
    const { user, logout } = useAuth();
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [message, setMessage] = useState(null);
    const [loading, setLoading] = useState(false);

    const handleDeleteAccount = async () => {
        if (deleteConfirmText !== 'DELETE') {
            setMessage({ type: 'error', text: 'Please type DELETE to confirm' });
            return;
        }

        setLoading(true);
        try {
            await authApi.deleteAccount();
            setMessage({ type: 'success', text: 'Account deletion requested. You will be logged out.' });

            // Log out after 2 seconds
            setTimeout(() => {
                logout();
            }, 2000);
        } catch (err) {
            setMessage({ type: 'error', text: err.message || 'Failed to delete account' });
        } finally {
            setLoading(false);
        }
    };

    const handleExportData = async () => {
        try {
            const response = await authApi.exportUserData();

            // Create download link
            const blob = new Blob([JSON.stringify(response.data, null, 2)], {
                type: 'application/json'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `spreadsheetr-data-${user.username}-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            setMessage({ type: 'success', text: 'Data exported successfully' });
        } catch (err) {
            setMessage({ type: 'error', text: err.message || 'Failed to export data' });
        }
    };

    return (
        <div className="account-settings">
            <div className="settings-header">
                <h1>Account Settings</h1>
                <p>Manage your account information and privacy settings</p>
            </div>

            {message && (
                <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
                    {message.text}
                </div>
            )}

            <div className="settings-sections">
                {/* Account Information */}
                <section className="settings-section">
                    <h2>Account Information</h2>
                    <div className="account-info">
                        <div className="info-item">
                            <label>Username</label>
                            <span>{user?.username}</span>
                        </div>
                        <div className="info-item">
                            <label>Role</label>
                            <span className="role-badge">{user?.role}</span>
                        </div>
                        <div className="info-item">
                            <label>Member Since</label>
                            <span>{formatDate(user?.created_at)}</span>
                        </div>
                    </div>
                </section>

                {/* Data Management */}
                <section className="settings-section">
                    <h2>Data Management</h2>
                    <div className="data-actions">
                        <div className="action-item">
                            <div className="action-info">
                                <h3>Export Your Data</h3>
                                <p>Download all your account data in JSON format</p>
                            </div>
                            <button
                                className="btn btn-secondary"
                                onClick={handleExportData}
                            >
                                Export Data
                            </button>
                        </div>
                    </div>
                </section>

                {/* Privacy Information */}
                <section className="settings-section">
                    <h2>Privacy Information</h2>
                    <div className="privacy-info">
                        <h3>What data we store:</h3>
                        <ul>
                            <li>Your username (not your real name)</li>
                            <li>Your organization name</li>
                            <li>Spreadsheet files and calculations you create</li>
                            <li>System audit logs for security</li>
                        </ul>

                        <h3>What we don't store:</h3>
                        <ul>
                            <li>Email addresses or phone numbers</li>
                            <li>Personal demographic information</li>
                            <li>Behavioral tracking data</li>
                            <li>Marketing or advertising data</li>
                        </ul>
                    </div>
                </section>

                {/* Danger Zone */}
                <section className="settings-section danger-zone">
                    <h2>Danger Zone</h2>
                    <div className="danger-actions">
                        <div className="action-item">
                            <div className="action-info">
                                <h3>Delete Account</h3>
                                <p>
                                    Permanently delete your account and all associated data.
                                    This action cannot be undone. Your data will be removed within 30 days.
                                </p>
                            </div>
                            <button
                                className="btn btn-danger"
                                onClick={() => setShowDeleteConfirm(true)}
                            >
                                Delete Account
                            </button>
                        </div>
                    </div>
                </section>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Delete Account</h3>
                            <button
                                className="modal-close"
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                ×
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="delete-warning">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                                    <line x1="12" y1="9" x2="12" y2="13" />
                                    <line x1="12" y1="17" x2="12.01" y2="17" />
                                </svg>
                                <h4>This action cannot be undone</h4>
                                <p>
                                    This will permanently delete your account, remove all your data,
                                    and revoke access to all spreadsheet templates and calculations.
                                </p>
                            </div>

                            <div className="confirm-input">
                                <label>Type <strong>DELETE</strong> to confirm:</label>
                                <input
                                    type="text"
                                    value={deleteConfirmText}
                                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                                    placeholder="DELETE"
                                    className="form-input"
                                />
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowDeleteConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-danger"
                                onClick={handleDeleteAccount}
                                disabled={loading || deleteConfirmText !== 'DELETE'}
                            >
                                {loading ? 'Deleting...' : 'Delete Account'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountSettings;